import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import "./ListingDetails.css";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import InputField from "../../form-fields/InputField";
import CurrencyInput from "../../form-fields/CurrencyInput";
import SelectField from "../../form-fields/SelectField";
import { GetImages } from "../../../utils/GetImages";
import Card from "../../card";
import RightSideModal from "../../Modal/right-side-modal";
import Uploader from "../../CustomUploader/Uploader";
import Toggle from "../../form-fields/toggle";
import SimpleSpinner from "../../../components/loader";
import FixedFooter from "../add-listing-footer/fixed-footer";
import {
  DocumentService,
  TemplateService,
  TemplateTypeEnum,
  UnitService,
} from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { UserLoginData } from "../../../components/common/charts/Enums";
import { NotificationType, PubSub, TopicsEnum } from "../../../utils/pubsub";
import {
  getUnitListingDetailByUnitId,
  createUnitListingDetail,
  updateUnitListingDetail,
  toggleUnitListingDetailStatus,
} from "../../../services/unitListingDetail.service";
import SimpleButton from "../../buttons/Simple-button";
import {
  MAX_LISTING_IMAGES,
  LISTING_IMAGE_EXTENSIONS,
  LISTING_IMAGE_MIME_TYPES,
  toPendingImageKey,
  toServerImageKey,
  canAddListingImage,
  reorderKeys,
  resolveOrderedImageIds,
  getListingImageUploadBlockMessage,
  DESCRIPTION_REQUIRED_MESSAGE,
  isQuillContentEmpty,
} from "./listingImageConstants";

const QUILL_MODULES = {
  toolbar: [
    [{ history: "undo" }, { history: "redo" }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["code-block"],
    ["clean"],
  ],
  history: {
    delay: 1000,
    maxStack: 100,
    userOnly: false,
  },
};

const QUILL_FORMATS = [
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "code-block",
];

// DocumentEntityTypesEnum.UnitListingDetail = 8 and DocumentTypesEnum.ListingImage = 14
// are not yet in the published client-api; use literals until CI regenerates the package.
const DOCUMENT_ENTITY_TYPE_UNIT_LISTING_DETAIL = 8;
const DOCUMENT_TYPE_LISTING_IMAGE = 14;

interface ListingFormValues {
  isListed: boolean;
  rent: string;
  template: string;
  description: string;
}

interface ImageUploadValues {
  propertyImage: any;
  imageTitle: string;
  isMainImage: boolean;
}

// Images already saved on the server (have a real document ID)
interface ServerImage {
  id: number;
  label: string;
  isMain: boolean;
  url: string;
}

// Images selected in the modal but not yet sent to the server
interface PendingImage {
  key: string;
  file: File;
  label: string;
  isMain: boolean;
  previewUrl: string;
}

interface Props {
  unitId?: number;
  // Bumped by the parent whenever unit details/images change elsewhere (e.g. the
  // Overview tab's Edit Unit save) so this tab's own unitId-keyed fetches re-run even
  // though unitId itself never changes across an in-place edit -- see
  // bug/ui-bugfix-batch4 item 2.
  refreshTrigger?: number;
}

const ListingDetails = ({ unitId: unitIdProp, refreshTrigger }: Props) => {
  const { id } = useParams<{ id: string }>();
  const unitId = unitIdProp ?? Number(id);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serverImages, setServerImages] = useState<ServerImage[]>([]);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [imageOrder, setImageOrder] = useState<string[]>([]);
  /** True after a user drag-reorder; gates sending orderedImageIds on save. */
  const [imageOrderDirty, setImageOrderDirty] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [listingDetailId, setListingDetailId] = useState<number | null>(null);
  const [templateData, setTemplateData] = useState<any[]>([]);
  const [unitData, setUnitData] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const uploadFormId = "upload-image-form";

  const organization_id = Number(sessionStorage.getItem(UserLoginData.organization_id));

  const mainMethods = useForm<ListingFormValues>({
    mode: "onChange",
    defaultValues: { isListed: false, rent: "", template: "", description: "" },
  });

  const modalMethods = useForm<ImageUploadValues>({
    mode: "onChange",
    defaultValues: { propertyImage: null, imageTitle: "", isMainImage: false },
  });

  const previewFormMethods = useForm({
    defaultValues: {
      previewFirstName: "",
      previewLastName: "",
      previewEmail: "",
      previewPhone: "",
      previewMessage: "",
    },
  });

  const templateOptions = useMemo(
    () => templateData.map((t: any) => ({ Id: t.id, Name: t.name })),
    [templateData]
  );

  const allImages = useMemo(() => {
    const serverMap = new Map(serverImages.map(img => [toServerImageKey(img.id), img]));
    const pendingMap = new Map(pendingImages.map(img => [toPendingImageKey(img.key), img]));

    return imageOrder
      .map(orderKey => {
        if (orderKey.startsWith("server:")) {
          const img = serverMap.get(orderKey);
          if (!img) return null;
          return { ...img, isPending: false as const };
        }
        const img = pendingMap.get(orderKey);
        if (!img) return null;
        return {
          id: img.key,
          label: img.label,
          isMain: img.isMain,
          url: img.previewUrl,
          isPending: true as const,
          key: img.key,
        };
      })
      .filter((img): img is NonNullable<typeof img> => img !== null);
  }, [imageOrder, serverImages, pendingImages]);

  useEffect(() => {
    if (!organization_id) return;
    apiHelper(
      TemplateService.getApiTemplateOrganizationlist,
      { showNotification: false },
      organization_id,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      `templateTypeId == ${TemplateTypeEnum.LeaseApplication}`
    ).then((res) => {
      setTemplateData(res?.data?.success || []);
    }).catch(() => { });
  }, [organization_id]);

  useEffect(() => {
    if (!unitId) return;
    apiHelper(UnitService.getApiUnit1, { showNotification: false }, unitId)
      .then((res) => { setUnitData(res?.data?.success); })
      .catch(() => { });
  }, [unitId, refreshTrigger]);

  const loadImagesFromListing = async (detail: any) => {
    if (!detail.images?.length) {
      setServerImages([]);
      setImageOrder(prev => prev.filter(key => key.startsWith("pending:")));
      setImageOrderDirty(false);
      return;
    }
    // Keep every server doc id even when base64 preview fails — backend
    // orderedImageIds validation requires the full active-document set.
    const loaded: ServerImage[] = await Promise.all(
      detail.images.map(async (img: any) => {
        const base64Res = await apiHelper(
          DocumentService.getApiDocumentBase641,
          { showNotification: false },
          img.id
        ).catch(() => null);
        const base64 = base64Res?.data?.success;
        return {
          id: img.id,
          label: img.fileName ?? "Image",
          isMain: img.isMain ?? false,
          url: base64 ? "data:image/jpeg;base64," + base64 : "",
        };
      })
    );
    setServerImages(loaded);
    setImageOrder(prev => {
      const pendingKeys = prev.filter(key => key.startsWith("pending:"));
      return [...loaded.map(img => toServerImageKey(img.id)), ...pendingKeys];
    });
    setImageOrderDirty(false);
  };

  useEffect(() => {
    if (!unitId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getUnitListingDetailByUnitId(unitId)
      .then(async (res) => {
        const detail = res?.success;
        if (detail) {
          setListingDetailId(detail.id);
          mainMethods.reset({
            isListed: detail.isActive,
            rent: String(detail.rent),
            template: detail.leaseApplicationTemplateId != null ? String(detail.leaseApplicationTemplateId) : "",
            description: detail.description ?? "",
          });
          await loadImagesFromListing(detail);
        }
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [unitId, refreshTrigger]);

  const uploadPendingImages = async (entityId: number): Promise<Record<string, number>> => {
    const pendingByKey = new Map(pendingImages.map(img => [toPendingImageKey(img.key), img]));
    const orderedPending = imageOrder
      .filter(key => key.startsWith("pending:"))
      .map(key => pendingByKey.get(key))
      .filter((img): img is PendingImage => img !== undefined);

    const pendingIdByKey: Record<string, number> = {};

    for (const pending of orderedPending) {
      try {
        const res = await apiHelper(
          DocumentService.postApiDocument,
          { showNotification: false },
          {
            EntityTypeId: DOCUMENT_ENTITY_TYPE_UNIT_LISTING_DETAIL,
            EntityId: entityId,
            DocumentTypeId: DOCUMENT_TYPE_LISTING_IMAGE,
            IsMain: pending.isMain,
            FileName: pending.label || pending.file.name,
            Document: pending.file,
          }
        );
        const newId = res?.data?.success?.id as number | undefined;
        if (typeof newId === "number" && newId > 0) {
          pendingIdByKey[pending.key] = newId;
        }
      } catch (err) {
        console.error("Failed to upload image", pending.label, err);
      }
    }
    setPendingImages([]);
    setImageOrder(prev => prev.filter(key => key.startsWith("server:")));
    return pendingIdByKey;
  };

  const onSubmit = async (data: ListingFormValues) => {
    const vm = {
      unitId,
      rent: parseFloat(data.rent) || 0,
      leaseApplicationTemplateId: data.template ? parseInt(data.template) : null,
      description: data.description,
    };

    setIsSubmitting(true);
    try {
      let resolvedListingId = listingDetailId;
      const isNew = listingDetailId === null;

      if (isNew) {
        const res = await createUnitListingDetail(vm);
        resolvedListingId = res?.success?.id ?? null;
        if (resolvedListingId) {
          setListingDetailId(resolvedListingId);
        }
      } else {
        // Only send order when user reordered and there are no pending uploads
        // (pending merge is persisted on the follow-up PUT after upload).
        const orderedImageIds = resolveOrderedImageIds(imageOrder);
        const includeOrder =
          imageOrderDirty &&
          pendingImages.length === 0 &&
          orderedImageIds.length > 0;
        await updateUnitListingDetail(listingDetailId, {
          ...vm,
          id: listingDetailId,
          ...(includeOrder ? { orderedImageIds } : {}),
        });
        if (includeOrder) {
          setImageOrderDirty(false);
        }
      }

      let pendingIdByKey: Record<string, number> = {};
      if (resolvedListingId && pendingImages.length > 0) {
        pendingIdByKey = await uploadPendingImages(resolvedListingId);
      }

      if (resolvedListingId) {
        const orderedImageIds = resolveOrderedImageIds(imageOrder, pendingIdByKey);
        const hasNewPendingIds = Object.keys(pendingIdByKey).length > 0;
        // Persist order after create, or when merging newly uploaded pending ids
        // (or when dirty on create-path). Skip non-reorder saves.
        const includeOrderFollowUp =
          orderedImageIds.length > 0 &&
          (isNew || hasNewPendingIds) &&
          (imageOrderDirty || hasNewPendingIds);
        if (includeOrderFollowUp) {
          await updateUnitListingDetail(resolvedListingId, {
            ...vm,
            id: resolvedListingId,
            orderedImageIds,
          });
          setImageOrderDirty(false);
        }

        const freshRes = await getUnitListingDetailByUnitId(unitId);
        if (freshRes?.success) {
          await loadImagesFromListing(freshRes.success);
        }
      }

      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: isNew ? "Listing created successfully" : "Listing updated successfully",
        type: NotificationType.Success,
      });

    } catch (err) {
      console.error("Failed to save listing detail", err);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to save listing. Please try again.",
        type: NotificationType.Failure,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleChange = async (newValue: boolean) => {
    mainMethods.setValue("isListed", newValue);
    if (listingDetailId !== null) {
      try {
        await toggleUnitListingDetailStatus(listingDetailId);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: newValue ? "Listing published successfully" : "Listing unpublished successfully",
          type: NotificationType.Success,
        });
      } catch (err) {
        console.error("Failed to toggle listing status", err);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Failed to update listing status",
          type: NotificationType.Failure,
        });
      }
    }
  };

  const handleDeleteServerImage = async (id: number) => {
    try {
      await apiHelper(DocumentService.deleteApiDocument, { showNotification: false }, id);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Image deleted successfully",
        type: NotificationType.Success,
      });
    } catch (err) {
      console.error("Failed to delete image", err);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to delete image",
        type: NotificationType.Failure,
      });
    }
    setServerImages(prev => prev.filter(img => img.id !== id));
    setImageOrder(prev => prev.filter(key => key !== toServerImageKey(id)));
  };

  const handleDeletePendingImage = (key: string) => {
    setPendingImages(prev => prev.filter(img => img.key !== key));
    setImageOrder(prev => prev.filter(orderKey => orderKey !== toPendingImageKey(key)));
  };

  const notifyImageLimitReached = () => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      message: `Maximum ${MAX_LISTING_IMAGES} images allowed`,
      type: NotificationType.Failure,
    });
  };

  const handleOpenUploadModal = () => {
    if (!canAddListingImage(allImages.length)) {
      notifyImageLimitReached();
      return;
    }
    setResetKey(prev => prev + 1);
    setIsModalOpen(true);
  };

  const handleImageDragStart = (index: number) => (e: React.DragEvent) => {
    dragIndexRef.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleImageDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === index) return;
    setImageOrder(prev => reorderKeys(prev, fromIndex, index));
    setImageOrderDirty(true);
    dragIndexRef.current = null;
    setDragIndex(null);
  };

  const handleImageDragEnd = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
  };

  // Modal Save: only append to local state — no API call
  const onImageUploadSubmit = (data: ImageUploadValues) => {
    if (!data.propertyImage) return;
    const blockMessage = getListingImageUploadBlockMessage(allImages.length, true);
    if (blockMessage) {
      notifyImageLimitReached();
      return;
    }

    const key = `pending-${Date.now()}`;
    const newPending: PendingImage = {
      key,
      file: data.propertyImage,
      label: data.imageTitle || "Untitled",
      isMain: data.isMainImage,
      previewUrl: URL.createObjectURL(data.propertyImage),
    };

    if (data.isMainImage) {
      setServerImages(prev => prev.map(img => ({ ...img, isMain: false })));
      setPendingImages(prev => prev.map(img => ({ ...img, isMain: false })).concat(newPending));
    } else {
      setPendingImages(prev => [...prev, newPending]);
    }
    setImageOrder(prev => [...prev, toPendingImageKey(key)]);

    modalMethods.reset();
    setResetKey(prev => prev + 1);
    setIsModalOpen(false);
  };

  const handlePreview = useCallback(() => {
    setPreviewImageIndex(0);
    setIsPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => setIsPreviewOpen(false), []);

  const prevPreviewImage = useCallback(() =>
    setPreviewImageIndex(i => Math.max(0, i - 1)), []);

  const nextPreviewImage = useCallback(() =>
    setPreviewImageIndex(i => Math.min(allImages.length - 1, i + 1)), [allImages.length]);

  if (loading) {
    return (
      <div className="listing-detail-loading-wrapper">
        <SimpleSpinner />
      </div>
    );
  }

  const currentRent = mainMethods.watch("rent");
  const currentDescription = mainMethods.watch("description");

  return (
    <>
      <div className="d-flex flex-column gap-3 full-page-content listing-detail-page">
        <FormProvider {...mainMethods}>
          <Card className="mx-0">
            <form id="listing-detail-form" onSubmit={mainMethods.handleSubmit(onSubmit)}>

              <div className="top-listing-header-top d-flex justify-content-start align-items-center mb-3">
                <h5 className="fw-bold mb-0 me-3">List Unit</h5>
                <Controller
                  name="isListed"
                  control={mainMethods.control}
                  render={({ field }) => (
                    <Toggle isChecked={field.value} onChange={handleToggleChange} />
                  )}
                />
              </div>

              <div className="listing-basic-details">
                <div className="row">
                  <div className="col-md-2">
                    <CurrencyInput name="rent" label="Rent" required placeholder="0.00" />
                  </div>
                  <div className="col-md-4">
                    <SelectField
                      name="template"
                      label="Lease Application Template"
                      required
                      options={templateOptions}
                      placeholderText="Select Template"
                    />
                  </div>
                  <div className="col-md-12 mt-3">
                    <label className="form-label fw-600">Description <span className="text-danger">*</span></label>
                    <Controller
                      name="description"
                      control={mainMethods.control}
                      rules={{
                        validate: (value) =>
                          !isQuillContentEmpty(value) || DESCRIPTION_REQUIRED_MESSAGE,
                      }}
                      render={({ field, fieldState }) => (
                        <div className="listing-description-field">
                          <div className="listing-description-editor">
                            <ReactQuill
                              value={field.value || ""}
                              onChange={(content) => field.onChange(content)}
                              formats={QUILL_FORMATS}
                              style={{ height: 200 }}
                            />
                          </div>
                          {fieldState.error?.message && (
                            <div className="listing-description-error text-danger small" role="alert">
                              {fieldState.error.message}
                            </div>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="listing-images-details mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-bold mb-0">Property Images</h5>
                  <SimpleButton
                    label="Upload Images"
                    onClick={handleOpenUploadModal}
                    isDisabled={!canAddListingImage(allImages.length)}
                  />
                </div>

                {allImages.length === 0 ? (
                  <div className="no-images-placeholder text-center py-5 border rounded">
                    <img src={GetImages.imageAvatar} className="image-avatar" alt="Empty" width="60" />
                    <p className="black fw-bold mt-2">No Images Uploaded</p>
                  </div>
                ) : (
                  <div className="images-grid-scroll">
                    <div className="d-flex gap-3 pb-2">
                      {allImages.map((img, index) => (
                        <div
                          key={img.isPending ? toPendingImageKey(String(img.id)) : toServerImageKey(img.id as number)}
                          className={`image-card-wrapper${dragIndex === index ? " image-card-dragging" : ""}`}
                          draggable={allImages.length > 1}
                          onDragStart={handleImageDragStart(index)}
                          onDragOver={handleImageDragOver}
                          onDrop={handleImageDrop(index)}
                          onDragEnd={handleImageDragEnd}
                          data-testid="listing-image-card"
                        >
                          <div className="image-card">
                            {allImages.length > 1 && (
                              <span className="image-drag-handle" title="Drag to reorder" aria-hidden="true">⋮⋮</span>
                            )}
                            <img
                              className="listing-images"
                              src={img.url}
                              alt={img.label}
                              draggable={false}
                            />
                            {img.isMain && <span className="main-star">★</span>}
                            <div className="image-actions">
                              <button
                                type="button"
                                className="action-btn"
                                draggable={false}
                                onDragStart={(e) => e.stopPropagation()}
                                onClick={() => {
                                if (img.isPending) handleDeletePendingImage(img.id as string);
                                else handleDeleteServerImage(img.id as number);
                              }}>
                                <img src={GetImages.deleteIconRed} width="14" alt="delete" draggable={false} />
                              </button>
                            </div>
                            <div className="image-label">{img.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="listing-images-hint mt-2">
                  Image format jpeg and png. Max 7 images size of 1 MB. Drag rows to reorder images.
                </p>
              </div>

            </form>
          </Card>

          <FixedFooter
            ShowCancelButton={false}
            onSubmit={mainMethods.handleSubmit(onSubmit)}
            loading={isSubmitting}
            buttonLabel={"Save"}
            isSubmitDisabled={isSubmitting || !mainMethods.formState.isDirty || !mainMethods.formState.isValid}
            ShowSavePostPaymentButton={true}
            onSubmitPostPayment={handlePreview}
            SavePostPaymentbuttonLabel="Preview"
          />
        </FormProvider>
      </div>

      <RightSideModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Upload Property Image"
        buttonlabel="Save"
        formId={uploadFormId}
        maxWidth={480}
        isDirty={modalMethods.formState.isDirty}
        isSubmitDisabled={!modalMethods.formState.isDirty || !modalMethods.formState.isValid}
      >
        <FormProvider {...modalMethods}>
          <form id={uploadFormId} onSubmit={modalMethods.handleSubmit(onImageUploadSubmit)}>
            <div className="p-4">
              <div className="mb-4 unit-listing-image-uploader">
                <Uploader
                  name="propertyImage"
                  maxSizeMB={1}
                  required
                  resetTrigger={resetKey}
                  allowedFormats={[...LISTING_IMAGE_MIME_TYPES]}
                  allowedExtensions={[...LISTING_IMAGE_EXTENSIONS]}
                />
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="primary-toggle-label">Set as main image</span>
                <Controller
                  name="isMainImage"
                  control={modalMethods.control}
                  render={({ field }) => (
                    <Toggle isChecked={field.value} onChange={field.onChange} />
                  )}
                />
              </div>
              <InputField
                name="imageTitle"
                label="Image Title"
                placeholder="e.g. Master Bedroom"
                validation={{
                  maxLength: { value: 50, message: "Image Title cannot exceed 50 characters" },
                }}
              />
            </div>
          </form>
        </FormProvider>
      </RightSideModal>

      {isPreviewOpen && (
        <div className="listing-preview-backdrop" onClick={handleClosePreview}>
          <div
            className="listing-preview-popup"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="listing-preview-modal-header">
              <span className="fw-semibold">Unit Listing Preview</span>
              <button
                type="button"
                className="listing-preview-x-btn"
                onClick={handleClosePreview}
              >
                ×
              </button>
            </div>

            {/* Scrollable body */}
            <div className="listing-preview-scroll-body">
              {/* Top: carousel + Request Viewing form */}
              <div className="d-flex listing-preview-top-section">
                {/* Left: image carousel */}
                <div className="listing-preview-left-col">
                  {allImages.length > 0 ? (
                    <div className="listing-preview-carousel">
                      <img
                        src={allImages[previewImageIndex]?.url}
                        alt={allImages[previewImageIndex]?.label}
                        className="listing-preview-carousel-img"
                      />
                      {allImages.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="preview-arrow-btn preview-arrow-prev"
                            onClick={prevPreviewImage}
                            disabled={previewImageIndex === 0}
                          >
                            &#8249;
                          </button>
                          <button
                            type="button"
                            className="preview-arrow-btn preview-arrow-next"
                            onClick={nextPreviewImage}
                            disabled={previewImageIndex === allImages.length - 1}
                          >
                            &#8250;
                          </button>
                        </>
                      )}
                      <div className="preview-img-caption">
                        {allImages[previewImageIndex]?.label}
                      </div>
                    </div>
                  ) : (
                    <div className="listing-preview-no-img d-flex align-items-center justify-content-center">
                      <img src={GetImages.imageAvatar} width="64" alt="No images" />
                    </div>
                  )}
                </div>

                {/* Right: Request Viewing form */}
                <div className="listing-preview-right-col">
                  <h4 className="fw-bold mb-1">Request Viewing</h4>
                  <p className="text-muted small mb-3">
                    Fill out the form below and we'll get back to you to confirm your viewing appointment.
                  </p>
                  <FormProvider {...previewFormMethods}>
                    <form onSubmit={previewFormMethods.handleSubmit(() => { })}>
                      <div className="row g-2">
                        <div className="col-6">
                          <InputField name="previewFirstName" label="First Name" required placeholder="Enter First Name" disabled />
                        </div>
                        <div className="col-6">
                          <InputField name="previewLastName" label="Last Name" required placeholder="Enter Last Name" disabled />
                        </div>
                        <div className="col-6">
                          <InputField name="previewEmail" label="Email Address" required placeholder="Enter Email Address" disabled />
                        </div>
                        <div className="col-6">
                          <InputField name="previewPhone" label="Contact No" required placeholder="Enter Contact No" disabled />
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold">
                            Preferred Dates &amp; Times (Optional)
                          </label>
                        </div>
                        <div className="col-6">
                          <InputField name="previewStartDate" label="Start Date" required placeholder="Select Start Date" disabled />
                        </div>
                        <div className="col-6">
                          <SelectField name="previewStartTime" label="Start Time" options={[{ Id: "9:00 AM", Name: "9:00 AM" }, { Id: "10:00 AM", Name: "10:00 AM" }]} placeholderText="Select time" disabled />
                        </div>


                        <div className="col-6">
                          <InputField name="previewEndDate" label="End Date" required placeholder="Select End Date" disabled />


                        </div>
                        <div className="col-6">
                          <SelectField className="w-100" name="previewEndTime" label="End Time" options={[{ Id: "5:00 PM", Name: "5:00 PM" }, { Id: "6:00 PM", Name: "6:00 PM" }]} placeholderText="Select time" disabled />
                        </div>












                        <div className="col-12">
                          <label className="form-label small fw-semibold">Message (Optional)</label>
                          <textarea
                            className="form-control"
                            rows={4}
                            placeholder="Any questions or special requirements?"
                            disabled
                          />
                        </div>
                      </div>
                      <SimpleButton label="Request Viewing" type="button" width="100%" className="mt-3 disabled-blue-btn" />
                    </form>
                  </FormProvider>
                  <p className="text-center text-muted small mt-2">
                    By submitting, you agree to be contacted about this property.
                  </p>
                </div>
              </div>

              {/* Property details */}
              <div className="listing-preview-property-section">
                <div className="row g-4">
                  <div className="col-md-7">
                    <h2 className="fw-bold mb-2">
                      {unitData?.unitNumber ? `Unit ${unitData.unitNumber}` : "Unit"}
                    </h2>
                    <p className="listing-preview-address mb-2">
                      <img src={GetImages.pinIcon} width="14" className="me-1" alt="Address" />
                      {[unitData?.streetAddress, unitData?.streetAddress]
                        .filter(Boolean).join(" – ") || "Address not available"}
                    </p>
                    <p className="listing-preview-specs mb-3">
                      {unitData?.numberOfBedrooms != null && (
                        <span className="preview-spec-item">
                          <img
                            src={GetImages.bedroomIcon}
                            width="14"
                            alt="Bedrooms"
                          />
                          <span>{unitData.numberOfBedrooms} Beds</span>
                        </span>
                      )}

                      {unitData?.numberOfBathrooms != null && (
                        <span className="preview-spec-item">
                          <img
                            src={GetImages.bathroomIcon}
                            width="14"
                            alt="Bathrooms"
                          />
                          <span>{unitData.numberOfBathrooms} Baths</span>
                        </span>
                      )}

                      {unitData?.size != null && (
                        <span className="preview-spec-item">
                          <img
                            src={GetImages.sqftIcon}
                            width="14"
                            alt="Size"
                          />
                          <span>{unitData.size} sqft</span>
                        </span>
                      )}
                    </p>
                    <h6 className="fw-bold mb-2">Description</h6>
                    <div
                      className="listing-preview-desc"
                      dangerouslySetInnerHTML={{ __html: currentDescription || "<em>No description provided.</em>" }}
                    />
                  </div>
                  <div className="col-md-5">
                    <div className="listing-preview-price fw-bold mb-2">

                      {currentRent
                        ? (
                          <div className="listing-preview-price-amount">
                            $
                            {parseFloat(currentRent).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            <span className="listing-preview-price-mo">/mo</span>
                          </div>
                          )
                        : <span className="text-muted fw-normal">Rent not set</span>
                      }
                      <SimpleButton className="disabled-blue-btn" label="Apply Now" type="button" width="100%" onClick={() => { }} />
                    </div>

                    <div className="listing-preview-map-placeholder mt-3">
                      <span className="text-muted small">Map preview</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed footer */}
            <div className="listing-preview-bottom-footer">
              <p className="text-muted small mb-0">
                Note: This is a preview based on your current settings. Save changes to update the live website.
              </p>
              <SimpleButton label="Close Preview" type="button" onClick={handleClosePreview} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListingDetails;